import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import VoxSmythIcons from '../VoxSmythIcons';
import { BookMiniCover } from './BookMiniCard';

/**
 * HomeContinueListeningHero — minimal "Continue Listening" surface for the
 * landing page (`/`). Reads from PlayerContext only (no library fetch) so it
 * renders instantly on app open. Hidden when nothing has been played yet.
 *
 * Tapping it navigates to /player?bookId={id} to restore the listening
 * context. Live progress fraction is read from the same provider so the
 * hero's progress bar stays in sync while the user is on /player.
 */
export const HomeContinueListeningHero = () => {
  const navigate = useNavigate();
  const player = usePlayer();
  const np = player?.nowPlaying;

  if (!np || !np.bookId) return null;

  const fraction = player.duration > 0
    ? Math.min(1, Math.max(0, player.currentTime / player.duration))
    : 0;

  const handleResume = () => {
    if (window.navigator && typeof window.navigator.vibrate === 'function') {
      try { window.navigator.vibrate(15); } catch (_) { /* noop */ }
    }
    navigate(`/player?bookId=${encodeURIComponent(np.bookId)}`);
  };

  return (
    <section
      data-testid="home-continue-listening-hero"
      style={{
        margin: '0 auto 36px',
        maxWidth: '900px',
        padding: '0 24px',
      }}
    >
      <button
        type="button"
        data-testid="home-continue-listening-resume-button"
        onClick={handleResume}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '18px',
          padding: '18px 22px',
          background: 'linear-gradient(135deg, rgba(215,179,119,0.16) 0%, rgba(215,179,119,0.06) 100%)',
          border: '1px solid rgba(215, 179, 119, 0.42)',
          borderRadius: '18px',
          color: '#F8FAFC',
          cursor: 'pointer',
          textAlign: 'left',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          fontFamily: 'Inter, sans-serif',
          position: 'relative',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 14px 32px rgba(215,179,119,0.32)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
        }}
      >
        {/* Cover — category-colored (sci-fi teal, romance mauve, etc.)
            via the shared BookMiniCover so the hero is consistent with
            every other Continue-Listening surface. */}
        <BookMiniCover book={{ title: np.title, genre: np.genre, cover: np.cover }} size="lg" />

        {/* Title + chapter + progress */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '11px',
            color: '#D7B377',
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            marginBottom: '4px',
          }}>
            Continue Listening
          </div>
          <div
            data-testid="home-continue-listening-title"
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: '20px',
              color: '#F8FAFC',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginBottom: '2px',
            }}
          >
            {np.title || 'Resume your book'}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#CBD5E0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginBottom: '10px',
          }}>
            {np.chapter || 'Chapter 1'}
          </div>
          <div style={{
            height: '4px',
            borderRadius: '2px',
            background: 'rgba(215, 179, 119, 0.18)',
            overflow: 'hidden',
          }}>
            <div
              data-testid="home-continue-listening-progress"
              style={{
                width: `${Math.max(2, Math.round(fraction * 100))}%`,
                height: '100%',
                borderRadius: '2px',
                background: 'linear-gradient(90deg, #D7B377, #F39C12)',
              }}
            />
          </div>
        </div>

        {/* Resume CTA pill */}
        <span style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'linear-gradient(135deg, #D7B377, #B8941F)',
          color: '#0F172A',
          fontWeight: 800,
          fontSize: '13px',
          letterSpacing: '0.05em',
          padding: '10px 18px',
          borderRadius: '22px',
          boxShadow: '0 4px 12px rgba(215,179,119,0.42)',
        }}>
          <VoxSmythIcons.Play size={14} color="#0F172A" />
          Resume
        </span>
      </button>
    </section>
  );
};

export default HomeContinueListeningHero;
